import { BoxIcon } from '@jouzen/ecom-components/icons/BoxIcon';
import { List } from '@jouzen/ecom-components/v3/List';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';

export interface PDPWhatsIncludedProps {
  readonly imageSrc: string;
  readonly localizedAlt: MessageKey;
  readonly titleKey: MessageKey;
  readonly listKey: MessageKey;
  readonly variant?: 'dark' | 'light';
}

const PDPWhatsIncluded = ({
  imageSrc,
  localizedAlt,
  titleKey,
  listKey,
  variant = 'dark',
}: PDPWhatsIncludedProps) => {
  const t = useTranslations();

  return (
    <div
      className={cx(
        'relative flex gap-x-4 md:justify-around lg:flex-col lg:items-center lg:gap-0',
        'before:absolute before:inset-0 before:bg-linear-to-t',
        'before:from-transparent before:from-70% before:to-gray-450 before:to-100%',
        'before:content-[""] lg:before:bg-none',
        {
          'before:bg-none': variant === 'light',
        },
      )}
    >
      <div className="max-w-[35%] md:max-w-full">
        <Image
          format="png"
          height={440}
          localizedAlt={localizedAlt}
          src={imageSrc}
          width={320}
        />
      </div>
      <div
        className={cx(
          'relative max-w-80 p-4 pl-0 md:h-fit md:w-full md:pl-4 lg:mt-4',
          {
            'rounded-lg md:bg-white/5': variant === 'dark',
            'rounded-3xl md:bg-white/40': variant === 'light',
          },
        )}
      >
        <div className="flex gap-x-3">
          <BoxIcon height={24} width={24} />
          <Typography className="mb-3 font-bold" color="inherit">
            {t(titleKey)}
          </Typography>
        </div>
        <List
          className="ml-9 flex flex-col gap-y-2"
          color="inherit"
          unstyled
          variant="eyebrow-small"
        >
          <RichText messageKey={listKey} />
        </List>
      </div>
    </div>
  );
};

export default PDPWhatsIncluded;
